/**
 * Palette de séries de charts pilotée par le thème du rapport.
 *
 * `derivePalette` dérive N couleurs de marque (primaire/secondaire) de façon
 * déterministe (aucun random) : interpolation primary→secondary sur les
 * premières couleurs, complétée par rotation d'angle d'or sur la teinte
 * primaire pour garantir la distinction visuelle.
 *
 * `PALETTE_PRESETS` fournit des jeux de couleurs prêts à l'emploi.
 * `resolveChartPalette` choisit la palette du thème si valide, sinon
 * la palette par défaut du builder (`CHART_COLORS`).
 */
import { CHART_COLORS } from './chartStyle';

const HEX_RE = /^#[0-9a-fA-F]{6}$/;
const GOLDEN_ANGLE = 137.508;

/** Parse un hex `#rrggbb` en HSL (h: 0-360, s/l: 0-100). Repli neutre si invalide. */
export function hexToHsl(hex: string): [number, number, number] {
  if (!HEX_RE.test(hex)) return [240, 50, 50];

  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  let h = 0;
  if (delta !== 0) {
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h *= 60;
    if (h < 0) h += 360;
  }

  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

  return [h, s * 100, l * 100];
}

/** Convertit HSL (h: any, s/l: 0-100) en hex `#rrggbb`, bornes clampées. */
export function hslToHex(h: number, s: number, l: number): string {
  const hue = ((h % 360) + 360) % 360;
  const sat = Math.min(100, Math.max(0, s)) / 100;
  const lum = Math.min(100, Math.max(0, l)) / 100;

  const c = (1 - Math.abs(2 * lum - 1)) * sat;
  const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
  const m = lum - c / 2;

  let r = 0;
  let g = 0;
  let b = 0;
  if (hue < 60) [r, g, b] = [c, x, 0];
  else if (hue < 120) [r, g, b] = [x, c, 0];
  else if (hue < 180) [r, g, b] = [0, c, x];
  else if (hue < 240) [r, g, b] = [0, x, c];
  else if (hue < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];

  const toByte = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0');

  return `#${toByte(r)}${toByte(g)}${toByte(b)}`;
}

/**
 * Dérive `count` couleurs cohérentes marque à partir de primary/secondary,
 * de façon déterministe et garantissant la distinction des couleurs générées.
 */
export function derivePalette(primary: string, secondary: string, count = 10): string[] {
  const [h1, s1, l1] = hexToHsl(primary);
  const [h2, s2, l2] = hexToHsl(secondary);

  const result: string[] = [];
  const seen = new Set<string>();

  const pushDistinct = (hex: string, fallbackHue: number) => {
    let candidate = hex;
    let hue = fallbackHue;
    let guard = 0;
    while (seen.has(candidate) && guard < 360) {
      hue = (hue + 17) % 360;
      candidate = hslToHex(hue, 60, 52);
      guard += 1;
    }
    seen.add(candidate);
    result.push(candidate);
  };

  // Moitié des couleurs : interpolation primary -> secondary.
  const interpCount = Math.ceil(count / 2);
  for (let i = 0; i < interpCount && result.length < count; i += 1) {
    const t = interpCount === 1 ? 0 : i / (interpCount - 1);
    // Interpolation de teinte par le chemin le plus court.
    let dh = h2 - h1;
    if (dh > 180) dh -= 360;
    if (dh < -180) dh += 360;
    const h = h1 + dh * t;
    const s = s1 + (s2 - s1) * t;
    const l = l1 + (l2 - l1) * t;
    pushDistinct(hslToHex(h, Math.min(80, Math.max(40, s)), Math.min(65, Math.max(40, l))), h);
  }

  // Reste : rotation d'angle d'or autour de la teinte primaire.
  let k = 1;
  while (result.length < count) {
    const h = h1 + k * GOLDEN_ANGLE;
    const s = 45 + (k % 4) * 7.5; // 45..75 approx
    const l = 45 + ((k * 3) % 4) * 5.5; // 45..62 approx
    pushDistinct(hslToHex(h, Math.min(75, Math.max(45, s)), Math.min(62, Math.max(45, l))), h);
    k += 1;
  }

  return result;
}

export interface ChartPalettePreset {
  id: string;
  label: string;
  colors: string[];
}

const monochromeColors = (hue: number): string[] => {
  const lums = [82, 72, 62, 52, 44, 36, 28, 20];
  return lums.map((l) => hslToHex(hue, 45, l));
};

export const PALETTE_PRESETS: ChartPalettePreset[] = [
  {
    id: 'corporate',
    label: 'Corporate',
    colors: ['#1E3A8A', '#3B82F6', '#0EA5E9', '#334155', '#64748B', '#4338CA', '#6366F1', '#94A3B8'],
  },
  {
    id: 'pastel',
    label: 'Pastel',
    colors: ['#A5B4FC', '#FCA5A5', '#FDE68A', '#BBF7D0', '#BAE6FD', '#F5D0FE', '#FBCFE8', '#FED7AA'],
  },
  {
    id: 'vif',
    label: 'Vif',
    colors: ['#DC2626', '#EA580C', '#FACC15', '#16A34A', '#0891B2', '#2563EB', '#9333EA', '#DB2777'],
  },
  {
    id: 'monochrome',
    label: 'Monochrome',
    colors: monochromeColors(230),
  },
];

/**
 * Résout la palette de séries à utiliser : la palette du thème si elle
 * contient au moins une couleur hex valide, sinon la palette par défaut.
 */
export function resolveChartPalette(themePalette?: string[]): string[] {
  const valid = (themePalette ?? []).filter((c) => HEX_RE.test(c));
  return valid.length ? valid : CHART_COLORS;
}

/**
 * Résout la palette effective d'un widget : override widget (preset id ou hex[])
 * si exploitable, sinon la palette du thème (déjà résolue).
 */
export function resolveWidgetPalette(
  override: string | string[] | undefined,
  themePalette: string[],
): string[] {
  if (typeof override === 'string') {
    const preset = PALETTE_PRESETS.find((p) => p.id === override);
    return preset ? preset.colors : themePalette;
  }
  if (Array.isArray(override)) {
    const valid = override.filter((c) => HEX_RE.test(c));
    return valid.length ? valid : themePalette;
  }
  return themePalette;
}
